import { queryClient } from 'services/http/queryClient';
import { logError } from 'services/logging/logging';
import { usePartnerParties } from 'services/repositories/partners/partners';
import { GenericError } from 'support/helpers/errors/errors';
import { OrderData } from 'support/helpers/orderForms/orderForms';
import { getPartyWithGS1orVAT } from 'support/helpers/parties/parties';
import { OrderFormDTO } from 'support/types';

/**
 * Fallback behavior that fills in the supplier data if there is none in the store already.
 * This is a fix for an issue which we can't replicate yet.
 * TODO: remove when we find the root cause
 */
export const __fallback__fillSupplierParty = async (
  orderData: OrderData,
  orderFormData: OrderFormDTO,
): Promise<OrderData> => {
  if (orderData.parties?.supplier && Object.values(orderData.parties?.supplier).length > 0) {
    return orderData;
  }

  logError(new GenericError('Order Forms: Supplier party missing', { orderData, orderFormData }));

  const supplierId = orderFormData.data?.supplier.id;
  if (!supplierId) {
    logError(new GenericError('Order Forms: No supplier ID available'));
    return orderData;
  }

  try {
    const supplierParties = await queryClient.fetchQuery({
      queryKey: usePartnerParties.getKey({ partnerId: supplierId }),
      queryFn: usePartnerParties.queryFn,
    });

    const supplierParty = getPartyWithGS1orVAT(supplierParties || []);

    if (!supplierParty) {
      throw new Error('No supplier party fetched');
    }

    return {
      ...orderData,
      parties: {
        buyer: orderData.parties?.buyer,
        ship_to: orderData.parties?.ship_to,
        bill_to: orderData.parties?.bill_to,
        supplier: {
          ...supplierParty,
          name: orderFormData.data?.supplier.name || '',
        },
      },
    };
  } catch (error) {
    logError(new GenericError('Order Forms: Failed to fetch supplier parties', { error }));
    return orderData;
  }
};
